import { cn, formatTime } from '@/lib/utils.js';
import { FileIcon, FileAudio } from 'lucide-react';
import { ToolCallDisplay } from './ToolCallDisplay.js';
import { ApprovalCard } from './ApprovalCard.js';
import { AskUserCard } from './AskUserCard.js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeHighlight from 'rehype-highlight';
import type { Message as MessageType } from '@/types/message.js';

interface MessageProps {
  message: MessageType;
}

export function Message({ message }: MessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isTool = message.role === 'tool';

  // Elicitation messages (ask_user) render as inline timeline items
  if (isTool && (message.elicitation || message.id.startsWith('elicitation-'))) {
    const approvalId = message.id.replace('elicitation-', '');
    return (
      <div className="flex flex-col animate-message-enter items-start my-2 px-4">
        <div className="w-full px-4">
          <AskUserCard
            approvalId={approvalId}
            elicitation={message.elicitation}
            sessionId={message.sessionId}
          />
        </div>
      </div>
    );
  }

  // Legacy ask_user support (will be deprecated)
  if (isTool && (message.askUser || message.id.startsWith('askuser-'))) {
    const requestId = message.askUser?.requestId || message.id.replace('askuser-', '');
    return (
      <div className="flex flex-col animate-message-enter items-start my-2 px-4">
        <div className="w-full px-4">
          <AskUserCard requestId={requestId} sessionId={message.sessionId} />
        </div>
      </div>
    );
  }

  // Tool messages - decide rendering based on STATE, not ID prefix
  if (isTool) {
    const hasResult = message.toolResult !== undefined;
    const isApprovalMessage = message.id.startsWith('approval-');

    // Show ApprovalCard ONLY when:
    // 1. It's an approval message AND
    // 2. Approval is still pending (no result yet and not rejected)
    const isPendingApproval = isApprovalMessage &&
      !hasResult &&
      message.approvalStatus !== 'approved' &&
      message.approvalStatus !== 'rejected';

    // Wrap in same container as assistant messages for alignment
    // px-4 outer + px-4 inner = 32px total, matching assistant message text start
    return (
      <div className="flex flex-col animate-message-enter items-start my-1 px-4">
        <div className="w-full px-4">
          {isPendingApproval ? (
            <ApprovalCard
              approvalId={message.id.replace('approval-', '')}
              toolName={message.toolName}
              toolArgs={message.toolArgs}
              approvalStatus={message.approvalStatus}
            />
          ) : (
            <ToolCallDisplay
              toolName={message.toolName}
              toolArgs={message.toolArgs}
              toolResult={message.toolResult}
              success={message.toolResultSuccess ?? false}
              requireApproval={message.requireApproval || isApprovalMessage}
              approvalStatus={message.approvalStatus}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col animate-message-enter mx-auto w-full max-w-4xl px-4 my-4 overflow-hidden',
        isUser && 'items-end',
        isAssistant && 'items-start'
      )}
    >
      {/* Message content */}
      <div
        className={cn(
          'px-4 py-3',
          isUser && 'bg-primary/10 rounded-lg max-w-3xl shadow-sm',
          isAssistant && 'w-full max-w-full overflow-x-auto'
        )}
      >
          {isUser ? (
            <>
              {/* Image attachment */}
              {message.role === 'user' && message.image && (
                <div className="mb-2">
                  <img
                    src={`data:${message.image.mimeType};base64,${message.image.base64}`}
                    alt="Uploaded image"
                    className="max-w-full rounded"
                    style={{ maxHeight: '400px', objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* File attachment */}
              {message.role === 'user' && message.file && (
                <div className="mb-2 flex items-center gap-2 text-sm">
                  {message.file.mimeType.startsWith('audio/') ? (
                    <FileAudio className="h-4 w-4 opacity-70" />
                  ) : (
                    <FileIcon className="h-4 w-4 opacity-70" />
                  )}
                  <span className="font-medium opacity-90 truncate">
                    {message.file.filename || 'Attachment'}
                  </span>
                </div>
              )}

              <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-foreground/95 break-words overflow-wrap-anywhere max-w-full">{message.content}</p>
            </>
          ) : (
            <div className="prose prose-base dark:prose-invert max-w-full prose-p:my-4 prose-p:first:mt-0 prose-p:last:mb-0 prose-headings:font-semibold prose-headings:tracking-tight prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-strong:font-semibold prose-pre:max-w-full prose-pre:overflow-x-auto">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  p: ({ children }) => <p className="leading-relaxed text-[15px] text-foreground/90 break-words overflow-x-auto max-w-full">{children}</p>,
                  ul: ({ children }) => <ul className="my-4 ml-6 space-y-2 text-[15px]">{children}</ul>,
                  ol: ({ children }) => <ol className="my-4 ml-6 space-y-2 text-[15px]">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed text-foreground/90">{children}</li>,
                  code: ({ inline, children, ...props }: any) =>
                    inline ? (
                      <code className="bg-primary/8 text-primary px-1.5 py-0.5 rounded text-[13px] font-mono font-medium break-all" {...props}>
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-muted/50 border border-border/30 p-4 rounded-lg text-[13px] font-mono overflow-x-auto my-4 leading-relaxed max-w-full whitespace-pre-wrap break-words" {...props}>
                        {children}
                      </code>
                    ),
                }}
              >
                {message.content || ''}
              </ReactMarkdown>
            </div>
          )}
      </div>

      {/* Metadata (timestamp only, very subtle) */}
      <div className={cn('text-[10px] text-muted-foreground/60 px-4 mt-1')}>
        <span>{formatTime(message.createdAt)}</span>
      </div>
    </div>
  );
}
